import React, {useEffect, useState} from 'react';
import {
  Typography,
  FormControl,
  FormLabel,
  Box,
  ToggleButton,
  ToggleButtonGroup,
  MenuItem,
  Select,
  TextField
} from '@mui/material';
import SentimentVeryDissatisfiedIcon from '@mui/icons-material/SentimentVeryDissatisfied';
import SentimentDissatisfiedIcon from '@mui/icons-material/SentimentDissatisfied';
import SentimentSatisfiedIcon from '@mui/icons-material/SentimentSatisfied';
import SentimentSatisfiedAltIcon from '@mui/icons-material/SentimentSatisfiedAltOutlined';
import SentimentVerySatisfiedIcon from '@mui/icons-material/SentimentVerySatisfied';
import SentimentNeutralIcon from '@mui/icons-material/SentimentNeutral';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';

interface CustomEditProps {
  [index: number]: {
    icon: React.ReactElement;
    label: string;
    id: number;
    key: string;
  };
}
interface RatingScaleProps {
  field: any;
}

const customIcons: CustomEditProps = {
  1: {
    icon: <SentimentVeryDissatisfiedIcon sx={{color: '#ff000a'}} />,
    label: 'Very Dissatisfied',
    id: 1,
    key: '1'
  },
  2: {
    icon: <SentimentDissatisfiedIcon color="error" />,
    label: 'Dissatisfied',
    id: 2,
    key: '2'
  },
  3: {
    icon: <SentimentNeutralIcon sx={{color: '#ffb300'}} />,
    label: 'Somewhat Dissatisfied',
    id: 3,
    key: '3'
  },
  4: {
    icon: <SentimentNeutralIcon sx={{color: '#e9ef0de0'}} />,
    label: 'Neutral',
    id: 4,
    key: '4'
  },
  5: {
    icon: <SentimentSatisfiedIcon sx={{color: '#38ef0de0'}} />,
    label: 'Somewhat Satisfied',
    id: 5,
    key: '5'
  },
  6: {
    icon: <SentimentSatisfiedIcon sx={{color: '#019f07'}} />,
    label: 'Satisfied',
    id: 6,
    key: '6'
  },
  7: {
    icon: <SentimentVerySatisfiedIcon sx={{color: 'darkgreen'}} />,
    label: 'Very Satisfied',
    id: 7,
    key: '7'
  }
};

const satisfactionIcons: CustomEditProps = {
  1: {
    icon: <SentimentVeryDissatisfiedIcon sx={{color: '#ff000a'}} />,
    label: 'Strongly disagree',
    id: 1,
    key: '1'
  },
  2: {
    icon: <SentimentDissatisfiedIcon color="error" />,
    label: 'Disagree',
    id: 2,
    key: '2'
  },
  3: {
    icon: <SentimentNeutralIcon sx={{color: '#ffb300'}} />,
    label: 'Somewhat disagree',
    id: 3,
    key: '3'
  },
  4: {
    icon: <SentimentSatisfiedIcon sx={{color: '#e9ef0de0'}} />,
    label: 'Neutral',
    id: 4,
    key: '4'
  },
  5: {
    icon: <SentimentSatisfiedAltIcon sx={{color: '#38ef0de0'}} />,
    label: 'Somewhat agree',
    id: 5,
    key: '5'
  },
  6: {
    icon: <SentimentSatisfiedIcon sx={{color: '#019f07'}} />,
    label: 'Agree',
    id: 6,
    key: '6'
  },
  7: {
    icon: <SentimentVerySatisfiedIcon sx={{color: 'darkgreen'}} />,
    label: 'Strongly agree',
    id: 7,
    key: '7'
  }
};

const frequencyIcons: CustomEditProps = {
  1: {
    icon: <FiberManualRecordIcon sx={{color: '#ff000a'}} />,
    label: 'Never',
    id: 1,
    key: '1'
  },
  2: {
    icon: <FiberManualRecordIcon color="error" />,
    label: 'Very rarely',
    id: 2,
    key: '2'
  },
  3: {
    icon: <FiberManualRecordIcon sx={{color: '#ffb300'}} />,
    label: 'Rarely',
    id: 3,
    key: '3'
  },
  4: {
    icon: <FiberManualRecordIcon sx={{color: '#e9ef0de0'}} />,
    label: 'Sometimes',
    id: 4,
    key: '4'
  },
  5: {
    icon: <FiberManualRecordIcon sx={{color: '#38ef0de0'}} />,
    label: 'Often',
    id: 5,
    key: '5'
  },
  6: {
    icon: <FiberManualRecordIcon sx={{color: '#019f07'}} />,
    label: 'Very often',
    id: 6,
    key: '6'
  },
  7: {
    icon: <FiberManualRecordIcon sx={{color: 'darkgreen'}} />,
    label: 'Always',
    id: 7,
    key: '7'
  }
};

const RatingScaleQuestionType: React.FC<RatingScaleProps> = ({field}) => {
  const [ratingScale, setRatingScale] = useState<number>(5);
  const [customIconsEdit, setCustomIconsEdit] =
    useState<CustomEditProps>(customIcons);
  const [ratingScaleType, setRatingScaleType] =
    useState<string>('Satisfaction');
  const handleRatingScaleChange = (
    event: React.MouseEvent<HTMLElement>,
    newRatingScale: number
  ) => {
    if (newRatingScale !== null) {
      setRatingScale(newRatingScale);
    }
  };
  useEffect(() => {
    field.onChange({
      ratingScale: ratingScale,
      ratingScaleType: ratingScaleType
    });
    if (ratingScaleType === 'Custom') {
      field.onChange({
        ratingScale: ratingScale,
        ratingScaleType: ratingScaleType,
        customIcons: customIconsEdit
      });
    } else if (ratingScaleType === 'Satisfaction') {
      field.onChange({
        ratingScale: ratingScale,
        ratingScaleType: ratingScaleType,
        customIcons: satisfactionIcons
      });
    } else if (ratingScaleType === 'Frequency') {
      field.onChange({
        ratingScale: ratingScale,
        ratingScaleType: ratingScaleType,
        customIcons: frequencyIcons
      });
    } else if (ratingScaleType === 'Agreement') {
      field.onChange({
        ratingScale: ratingScale,
        ratingScaleType: ratingScaleType,
        customIcons: customIcons
      });
    }
  }, [ratingScale, ratingScaleType]);

  const getIconsForScale = (scale: number, type: string) => {
    let icons;
    if (type === 'Satisfaction') {
      icons = satisfactionIcons;
    } else if (type === 'Frequency') {
      icons = frequencyIcons;
    } else if (type === 'Agreement') {
      icons = customIcons;
    } else {
      icons = customIconsEdit;
    }
    switch (scale) {
      case 3:
        return [icons[1], icons[4], icons[7]];
      case 5:
        return [icons[1], icons[2], icons[4], icons[5], icons[7]];
      case 7:
        return [
          icons[1],
          icons[2],
          icons[3],
          icons[4],
          icons[5],
          icons[6],
          icons[7]
        ];
      default:
        return [];
    }
  };

  const handleCustomLabelChange = (id: number, value: string) => {
    setCustomIconsEdit(prev => ({
      ...prev,
      [id]: {...prev[id], label: value}
    }));
  };

  return (
    <>
      <FormControl component="fieldset" margin="normal">
        <FormLabel component="legend">Select your rating scale</FormLabel>
        <ToggleButtonGroup
          value={ratingScale}
          sx={{maxWidth: '13vh'}}
          exclusive
          onChange={handleRatingScaleChange}
          aria-label="rating scale">
          <ToggleButton value={3}>3</ToggleButton>
          <ToggleButton value={5}>5</ToggleButton>
          <ToggleButton value={7}>7</ToggleButton>
        </ToggleButtonGroup>
      </FormControl>
      <FormControl fullWidth margin="normal">
        <FormLabel>Rating scale type</FormLabel>
        <Select
          value={ratingScaleType}
          onChange={e => setRatingScaleType(e.target.value)}>
          <MenuItem value="Agreement">Agreement</MenuItem>
          <MenuItem value="Frequency">Frequency</MenuItem>
          <MenuItem value="Satisfaction">Satisfaction</MenuItem>
          <MenuItem value="Custom">Custom</MenuItem>
        </Select>
      </FormControl>
      {(ratingScaleType === 'Satisfaction' ||
        ratingScaleType === 'Frequency' ||
        ratingScaleType === 'Agreement' ||
        ratingScaleType === 'Custom') && (
        <Box sx={{display: 'flex', justifyContent: 'space-around', mt: 2}}>
          {getIconsForScale(ratingScale, ratingScaleType).map(icon => (
            <Box key={icon.id} sx={{textAlign: 'center'}}>
              {icon.icon}
              {ratingScaleType !== 'Custom' ? (
                <Typography
                  variant="body2"
                  sx={{
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}>
                  {icon.label}
                </Typography>
              ) : (
                <TextField
                  value={icon.label}
                  onChange={e =>
                    handleCustomLabelChange(icon.id, e.target.value)
                  }
                  size="small"
                  variant="outlined"
                />
              )}
            </Box>
          ))}
        </Box>
      )}
    </>
  );
};

export default RatingScaleQuestionType;
